import { useGetRiskScoresV1RiskScoresGet } from '@/api/generated-catch-backend/risk-scores/risk-scores';
import { useGetTestSuggestionsV1TestSuggestionsGet } from '@/api/generated-catch-backend/test-suggestions/test-suggestions';
import { useGetActionPlanInterventionsV1ActionPlanInterventionsGet } from '@/api/generated-catch-backend/action-plan-interventions/action-plan-interventions';
import { InterventionStatus, TestSuggestionStatus, LabOrderListResponse } from '@/api/generated-catch-backend/model';
import {
  useRiskFactorsByCancerControllerGetUserRisk,
  useUserRiskControllerGetUserRisk,
} from '@/api/generated/default/default';
import { TypeOfCancer } from '@/components/icons/icon-catch/types';
import { RISK_LOWER_THRESHOLD, RISK_UPPER_THRESHOLD } from '@/constants/risks';
import { formatCancerTypeNameWithCapitalization } from '@/utils';
import { risksByCancerToSorted } from '@/utils/risks/sort-cancer-risks';
import { useGetBiomarkersV1BiomarkersGet } from '@/api/generated-catch-backend/biomarkers/biomarkers';
import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriticalLabNotifications } from '../../../notifications';
import { ROUTE_LABS_ALL } from '@/constants/routes';

export enum RiskLevel {
  Above = 'above',
  Below = 'below',
  Average = 'average',
}

export default function useDashboardBodyController(
  labOrdersData?: LabOrderListResponse,
  isEligibleForLabOrder?: boolean
) {
  const navigate = useNavigate();

  /**** Risks ****/

  const { data: riskData } = useUserRiskControllerGetUserRisk();
  const { data: riskScoresData } = useGetRiskScoresV1RiskScoresGet();
  const { data: testSuggestionData } = useGetTestSuggestionsV1TestSuggestionsGet({
    status: [TestSuggestionStatus.surfaced]
  });
  const {data: recommendedActionsData } = useGetActionPlanInterventionsV1ActionPlanInterventionsGet({
    status: [InterventionStatus.AVAILABLE, InterventionStatus.IN_PROGRESS]
  });

  /**** Critical Lab Notifications ****/

  const { notifications, dismissNotification, isDismissing } = useCriticalLabNotifications();

  const riskDataByCancer = riskData?.risk.risks ?? [];

  type ProcessedRiskData = Record<TypeOfCancer, { risk: number; level: RiskLevel }>;

  const processedRiskDataByCancer = riskDataByCancer.reduce((acc, { cancer, risk, averageRisk, potentialRisk }) => {
    acc[cancer] = {
      risk: risk,
      level: _getRiskLevel(risk, averageRisk),
    };
    return acc;
  }, {} as ProcessedRiskData);

  /*** Risk Factors ***/

  const { data: riskFactorsByCancer } = useRiskFactorsByCancerControllerGetUserRisk();
  const sortedRiskFactorsByCancer = risksByCancerToSorted(riskFactorsByCancer);

  const risks = sortedRiskFactorsByCancer?.factorsPerCancer.map(({ cancerType, riskFactorsSummary }) => ({
    cancerId: cancerType,
    name: formatCancerTypeNameWithCapitalization(cancerType),
    level: processedRiskDataByCancer?.[cancerType]?.level ?? RiskLevel.Average,
    risk:
      cancerType === 'overall'
        ? (riskData?.risk.overallRisk ?? 0)
        : (processedRiskDataByCancer?.[cancerType]?.risk ?? 0),
    /**
     * The positiveImpactRiskFactorsCount negatively affects the person,
     * represents a positive number risk and so, increases the overall risk.
     *
     * The negativeImpactRiskFactorsCount is beneficial for the person,
     * represents a negative number risk and so decreases the overall risk.
     */
    increasing: riskFactorsSummary.positiveImpactRiskFactorsCount,
    decreasing: riskFactorsSummary.negativeImpactRiskFactorsCount,
  }));

  const cancersCount = risks?.reduce<{
    cancersAbove: number;
    cancersBelow: number;
    cancersAverage: number;
  }>(
    (acc, { level }) => {
      if (level === RiskLevel.Above) {
        return { ...acc, cancersAbove: acc.cancersAbove + 1 };
      } else if (level === RiskLevel.Below) {
        return { ...acc, cancersBelow: acc.cancersBelow + 1 };
      } else {
        return { ...acc, cancersAverage: acc.cancersAverage + 1 };
      }
    },
    { cancersAbove: 0, cancersBelow: 0, cancersAverage: 0 },
  );

  /*** Step Size ***/

  const maxFactorsCount = Math.max(...(risks?.map(({ increasing, decreasing }) => increasing + decreasing) ?? []));

  risks?.sort((a, b) => a.cancerId.localeCompare(b.cancerId));

  const stepSize = 100 / maxFactorsCount;

  /*** Lab Orders ***/

  // Use lab orders data passed from parent instead of fetching
  const orders = labOrdersData?.data || [];
  const latestOrder = orders[0];

  // Check if lab results are complete
  const hasCompleteLabResults = latestOrder?.status?.toLowerCase().includes('complete');

  /*** Biomarker Counts ***/

  const { data: biomarkersData } = useGetBiomarkersV1BiomarkersGet();

  const biomarkerCounts = useMemo(() => {
    if (!biomarkersData?.summary) {
      return { optimal: 0, normal: 0, outOfRange: 0 };
    }

    return {
      optimal: biomarkersData.summary.optimal ?? 0,
      normal: biomarkersData.summary.normal ?? 0,
      // Combine out_of_range and critical counts
      outOfRange: (biomarkersData.summary.outOfRange ?? 0) + (biomarkersData.summary.critical ?? 0),
    };
  }, [biomarkersData]);

  /**** Notification Handlers ****/

  const handleViewResults = useCallback(
    async (notificationId: string) => {
      await dismissNotification(notificationId);
      navigate(`${ROUTE_LABS_ALL}?filter=critical`);
    },
    [dismissNotification, navigate],
  );

  const handleDismissNotification = useCallback(
    async (notificationId: string) => {
      await dismissNotification(notificationId);
    },
    [dismissNotification],
  );

  return {
    risks,
    riskScoresData,
    testSuggestionData,
    recommendedActionsData,
    cancersAbove: cancersCount?.cancersAbove ?? 0,
    cancersBelow: cancersCount?.cancersBelow ?? 0,
    cancersAverage: cancersCount?.cancersAverage ?? 0,
    stepSize,
    hasCompleteLabResults,
    biomarkerCounts,
    isEligibleForLabOrder,
    // Critical lab notifications
    notifications,
    isDismissingNotification: isDismissing,
    handleViewResults,
    handleDismissNotification,
  };
}

function _getRiskLevel(risk: number, averageRisk: number): RiskLevel {
  if (risk > averageRisk * RISK_UPPER_THRESHOLD) {
    return RiskLevel.Above;
  } else if (risk < averageRisk * RISK_LOWER_THRESHOLD) {
    return RiskLevel.Below;
  } else {
    return RiskLevel.Average;
  }
}
